import React from "react";
import { TrendCurveChartData } from "../../../interface/DesignerInterface";
import { TREND_CURVE_LEGEND_MAX_ITEMS } from "../../../constants/trendCurveConstants";
import { getTrendCurveLegendItems } from "../../../utils/trendSidebarGraphUtils/trendCurveChartHelpers";
import { formatTrendHeaderChips } from "../../../utils/designerPage-utils";

type TrendCurveLegendProps = {
  datasets: TrendCurveChartData["datasets"];
  expanded?: boolean;
  width?: number;
};

const TrendCurveLegend: React.FC<TrendCurveLegendProps> = ({
  datasets,
  expanded = false,
  width,
}) => {
  const items = getTrendCurveLegendItems(
    datasets,
    TREND_CURVE_LEGEND_MAX_ITEMS
  );
  if (!items.length) return null;

  return (
    <aside
      className={`flex h-full flex-col rounded-md border border-gray-300 bg-gray-50 font-poppins ${
        expanded ? "px-3 pt-2.5 pb-4" : "px-2 pt-1.5 pb-3"
      }`}
      style={
        width != null ? { width, minWidth: width, maxWidth: width } : undefined
      }
      aria-label={
        items.length === 1
          ? "Trend legend"
          : `Top ${items.length} trends legend`
      }
    >
      <div
        className={`shrink-0 font-semibold text-gray-700 ${
          expanded ? "mb-1 py-2 text-sm" : "mb-1.5 text-[10px]"
        }`}
      >
        {items.length === 1 ? "Legend" : `Top ${items.length} trends`}
      </div>
      <ul
        className={`min-h-0 flex-1 overflow-y-auto ${
          expanded ? "space-y-2.5" : "space-y-1"
        }`}
      >
        {items.map((dataset, index) => {
          const tNoPrefix =
            dataset.tNo != null ? `T${dataset.tNo}. ` : `${index + 1}. `;
          const shortName = formatTrendHeaderChips(dataset.label);
          const displayLabel = `${tNoPrefix}${shortName}`;

          return (
            <li
              key={`${dataset.tNo ?? index}-${dataset.label}`}
              className="flex items-start gap-1.5 px-0.5"
              title={`${tNoPrefix}${dataset.label}`}
            >
              <span
                className="mt-1.5 inline-flex shrink-0 items-center"
                aria-hidden
              >
                <span
                  className="block rounded-full"
                  style={{
                    width: expanded ? 18 : 14,
                    height: expanded ? 3 : 2.5,
                    backgroundColor: dataset.borderColor,
                  }}
                />
              </span>
              <span
                className={`min-w-0 break-words text-gray-800 ${
                  expanded
                    ? "text-xs leading-relaxed"
                    : "text-[10px] leading-snug"
                }`}
              >
                {displayLabel}
              </span>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default TrendCurveLegend;
